import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Upload, Save, RefreshCw, Settings, Plus, Trash2, Copy, FileJson, PackageOpen, X, LogOut } from 'lucide-react';
import JSZip from 'jszip';
import ControllerSection from './ControllerSection';
import AdvancedSection from './AdvancedSection';
import DeviceSection from './DeviceSection';
import ProfileSelector from './ProfileSelector';
import AddCategoryDialog from './AddCategoryDialog';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster.jsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Configurator = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check authentication on component mount
  useEffect(() => {
    if (sessionStorage.getItem('crestron_authenticated') !== 'true') {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('crestron_authenticated');
    navigate('/login');
  };
  const [suiteNumber, setSuiteNumber] = useState('');
  const [profileName, setProfileName] = useState('');
  const [currentConfigId, setCurrentConfigId] = useState(null);
  const [controller, setController] = useState({
    name: '',
    ip_address: '',
    subnet: '',
    gateway: '',
    dns: ''
  });
  const [advanced, setAdvanced] = useState({
    ntp_server: '',
    http_port: '',
    https_port: '',
    custom_param: ''
  });
  const [lights, setLights] = useState([]);
  const [shades, setShades] = useState([]);
  const [climate, setClimate] = useState([]);
  const [audio, setAudio] = useState([]);
  const [video, setVideo] = useState([]);
  const [relays, setRelays] = useState([]);
  const [security, setSecurity] = useState([]);
  const [customDevices, setCustomDevices] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isNewConfigMode, setIsNewConfigMode] = useState(false);

  useEffect(() => {
    loadProfiles();
    loadDefaults();
    loadCustomCategories();
  }, []);

  useEffect(() => {
    if (suiteNumber) {
      setController(prev => ({
        ...prev,
        name: `Suite ${suiteNumber} Processor`
      }));
    }
  }, [suiteNumber]);

  const loadProfiles = async () => {
    try {
      const response = await axios.get(`${API}/configurations`);
      setProfiles(response.data);
    } catch (error) {
      console.error('Error loading profiles:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profiles',
        variant: 'destructive'
      });
    }
  };

  const loadDefaults = async () => {
    try {
      const response = await axios.get(`${API}/defaults`);
      setDefaults(response.data);
    } catch (error) {
      console.error('Error loading defaults:', error);
    }
  };

  const loadCustomCategories = async () => {
    try {
      const response = await axios.get(`${API}/custom-categories`);
      setCustomCategories(response.data);
      
      // Initialize custom devices state for existing categories
      const currentCustomDevices = { ...customDevices };
      response.data.forEach(category => {
        if (!currentCustomDevices[category.category_name]) {
          currentCustomDevices[category.category_name] = [];
        }
      });
      setCustomDevices(currentCustomDevices);
    } catch (error) {
      console.error('Error loading custom categories:', error);
    }
  };

  const loadConfiguration = async (configId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/configurations/${configId}`);
      const config = response.data;
      
      setCurrentConfigId(config.id);
      setProfileName(config.profile_name);
      setSuiteNumber(config.suite_number);
      setController(config.controller);
      setAdvanced(config.advanced);
      setLights(config.lights || []);
      setShades(config.shades || []);
      setClimate(config.climate || []);
      setAudio(config.audio || []);
      setVideo(config.video || []);
      setRelays(config.relays || []);
      setSecurity(config.security || []);
      setCustomDevices(config.custom_devices || {});
      
      // Exit new config mode when loading full profile
      setIsNewConfigMode(false);
      
      toast({
        title: 'Success',
        description: 'Configuration loaded successfully'
      });
    } catch (error) {
      console.error('Error loading configuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to load configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    if (!profileName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a profile name',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      const configData = {
        profile_name: profileName,
        suite_number: suiteNumber,
        controller,
        advanced,
        lights,
        shades,
        climate,
        audio,
        video,
        relays,
        security,
        custom_devices: customDevices
      };

      let response;
      if (currentConfigId) {
        response = await axios.put(`${API}/configurations/${currentConfigId}`, configData);
        toast({
          title: 'Success',
          description: 'Configuration updated successfully'
        });
      } else {
        response = await axios.post(`${API}/configurations`, configData);
        setCurrentConfigId(response.data.id);
        toast({
          title: 'Success',
          description: 'Configuration saved successfully'
        });
      }
      
      await loadProfiles();
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createNewConfiguration = () => {
    setCurrentConfigId(null);
    setProfileName('');
    setSuiteNumber('');
    setController({
      name: '',
      ip_address: '',
      subnet: '',
      gateway: '',
      dns: ''
    });
    setAdvanced({
      ntp_server: '',
      http_port: '',
      https_port: '',
      custom_param: ''
    });
    setLights([]);
    setShades([]);
    setClimate([]);
    setAudio([]);
    setVideo([]);
    setRelays([]);
    setSecurity([]);
    setCustomDevices({});
    
    // Set new config mode to highlight "Load as Template"
    setIsNewConfigMode(true);
    
    toast({
      title: 'Info',
      description: 'New configuration started - Enter profile name to begin'
    });
    
    // Focus on profile name field after a short delay
    setTimeout(() => {
      const profileNameInput = document.querySelector('[data-testid="profile-name-input"]');
      if (profileNameInput) {
        profileNameInput.focus();
        profileNameInput.select();
      }
    }, 100);
  };

  // Auto-extract suite number from profile name
  const handleProfileNameChange = (value) => {
    setProfileName(value);
    
    // Extract suite number from profile name (looks for numbers)
    const suiteMatch = value.match(/(?:suite\s*)?(\d+)/i);
    if (suiteMatch && suiteMatch[1]) {
      setSuiteNumber(suiteMatch[1]);
    }
  };

  const loadConfigurationAsTemplate = async (configId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/configurations/${configId}`);
      const config = response.data;
      
      // Load device configurations and settings but keep current profile name and suite number
      const currentProfileName = profileName;
      const currentSuiteNumber = suiteNumber;
      
      setController(config.controller);
      setAdvanced(config.advanced);
      setLights(config.lights || []);
      setShades(config.shades || []);
      setClimate(config.climate || []);
      setAudio(config.audio || []);
      setVideo(config.video || []);
      setRelays(config.relays || []);
      setSecurity(config.security || []);
      setCustomDevices(config.custom_devices || {});
      
      // Restore the profile name and suite number
      setProfileName(currentProfileName);
      setSuiteNumber(currentSuiteNumber);
      
      // Update processor name if we have a suite number
      if (currentSuiteNumber) {
        setController(prev => ({
          ...prev,
          name: `Suite ${currentSuiteNumber} Processor`
        }));
      }
      
      toast({
        title: 'Template Loaded',
        description: `Loaded settings from "${config.profile_name}" as template`
      });
    } catch (error) {
      console.error('Error loading configuration as template:', error);
      toast({
        title: 'Error',
        description: 'Failed to load configuration template',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportConfiguration = async () => {
    if (!currentConfigId) {
      toast({
        title: 'Error',
        description: 'Please save the configuration first',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await axios.get(`${API}/configurations/${currentConfigId}/export`, {
        responseType: 'blob'
      });
      
      const fileName = `${profileName.replace(/ /g, '_')}.json`;
      const blob = new Blob([response.data], { type: 'application/json' });
      
      // Check if we're on mobile (iPhone/iPad/Android)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // For mobile devices, always use direct download
      if (isMobile) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL after download
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        
        toast({
          title: 'Success',
          description: 'Configuration downloaded to your device'
        });
        return;
      }
      
      // For desktop: Try File System Access API first, fallback to download
      if ('showSaveFilePicker' in window && window.isSecureContext) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [
              {
                description: 'JSON Files',
                accept: {
                  'application/json': ['.json'],
                },
              },
            ],
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          toast({
            title: 'Success',
            description: 'Configuration saved to your chosen location'
          });
          return;
        } catch (err) {
          // User cancelled the dialog or picker not available
          if (err.name === 'AbortError') {
            console.log('User cancelled file save dialog');
            return;
          }
          // If picker fails, fall through to fallback
          console.log('File picker not available, using fallback:', err.message);
        }
      }
      
      // Fallback: Direct download (works everywhere)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL after download
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      
      toast({
        title: 'Success',
        description: 'Configuration downloaded to Downloads folder'
      });
    } catch (error) {
      console.error('Error exporting configuration:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to export configuration. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const importConfiguration = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const text = await file.text();
      const configData = JSON.parse(text);
      
      const response = await axios.post(`${API}/configurations/import`, configData);
      
      setCurrentConfigId(response.data.id);
      setProfileName(response.data.profile_name);
      setSuiteNumber(response.data.suite_number);
      setController(response.data.controller);
      setAdvanced(response.data.advanced);
      setLights(response.data.lights || []);
      setShades(response.data.shades || []);
      setClimate(response.data.climate || []);
      setAudio(response.data.audio || []);
      setVideo(response.data.video || []);
      setRelays(response.data.relays || []);
      setSecurity(response.data.security || []);
      setCustomDevices(response.data.custom_devices || {});
      
      await loadProfiles();
      
      toast({
        title: 'Success',
        description: 'Configuration imported successfully'
      });
    } catch (error) {
      console.error('Error importing configuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to import configuration. Please check the file format.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const downloadAllConfigurations = async () => {
    if (profiles.length === 0) {
      toast({
        title: 'Info',
        description: 'No configurations to download',
      });
      return;
    }

    try {
      setLoading(true);
      const zip = new JSZip();
      
      // Create a folder for all configurations
      const folder = zip.folder('Crestron_Configurations');
      
      // Fetch and add each configuration to the zip
      for (const profile of profiles) {
        const response = await axios.get(`${API}/configurations/${profile.id}`, {
          responseType: 'json'
        });
        
        const fileName = `${profile.profile_name.replace(/[<>:"/\\|?*]/g, '_')}.json`;
        const jsonData = JSON.stringify(response.data, null, 2);
        folder.file(fileName, jsonData);
      }
      
      // Generate the zip file
      const content = await zip.generateAsync({ type: 'blob' });
      
      // Create filename with date
      const date = new Date().toISOString().split('T')[0];
      const zipFileName = `Crestron_All_Configurations_${date}.zip`;
      
      // Check if we're on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Mobile: Direct download
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', zipFileName);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        
        toast({
          title: 'Success',
          description: `Downloaded ${profiles.length} configurations as ZIP`
        });
        return;
      }
      
      // Desktop: Try file picker first
      if ('showSaveFilePicker' in window && window.isSecureContext) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: zipFileName,
            types: [
              {
                description: 'ZIP Files',
                accept: {
                  'application/zip': ['.zip'],
                },
              },
            ],
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(content);
          await writable.close();
          
          toast({
            title: 'Success',
            description: `Downloaded ${profiles.length} configurations as ZIP`
          });
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            return; // User cancelled
          }
          // Fall through to direct download
        }
      }
      
      // Fallback: Direct download
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', zipFileName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      
      toast({
        title: 'Success',
        description: `Downloaded ${profiles.length} configurations as ZIP`
      });
    } catch (error) {
      console.error('Error downloading all configurations:', error);
      toast({
        title: 'Error',
        description: 'Failed to download all configurations. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveAsDefaults = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/defaults`, {
        controller,
        advanced
      });
      
      await loadDefaults();
      
      toast({
        title: 'Success',
        description: 'Current settings saved as defaults'
      });
    } catch (error) {
      console.error('Error saving defaults:', error);
      toast({
        title: 'Error',
        description: 'Failed to save defaults',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyDefaults = () => {
    if (defaults) {
      setController(defaults.controller);
      setAdvanced(defaults.advanced);
      
      toast({
        title: 'Success',
        description: 'Defaults applied successfully'
      });
    } else {
      toast({
        title: 'Info',
        description: 'No defaults saved yet'
      });
    }
  };

  const deleteCustomCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/custom-categories/${categoryId}`);
      await loadCustomCategories();
      
      toast({
        title: 'Success',
        description: 'Custom category deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting custom category:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete custom category',
        variant: 'destructive'
      });
    }
  };

  const createCustomCategory = async (categoryName, fields) => {
    try {
      setLoading(true);
      
      // Transform fields to the format expected by backend
      const formattedFields = fields.map(field => ({
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required || false,
        options: field.options || []
      }));

      await axios.post(`${API}/custom-categories?category_name=${encodeURIComponent(categoryName)}`, formattedFields);
      
      await loadCustomCategories();
      setShowAddCategoryDialog(false);
      
      // Initialize empty device list for new category
      setCustomDevices(prev => ({
        ...prev,
        [categoryName]: []
      }));
      
      toast({
        title: 'Success',
        description: `Custom category "${categoryName}" created successfully`
      });
    } catch (error) {
      console.error('Error creating custom category:', error);
      toast({
        title: 'Error',
        description: 'Failed to create custom category',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
                <Settings className="w-8 h-8 text-blue-600" />
                Crestron-System-Setup-Configurator
              </h1>
              <p className="text-sm text-gray-600 mt-1">developed by Erwin Mayr</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          
          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            <Button
              onClick={createNewConfiguration}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="new-config-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
            <Button
              onClick={saveConfiguration}
              disabled={loading}
              size="sm"
              className="w-full"
              data-testid="save-config-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
            <Button
              onClick={exportConfiguration}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
            <Button
              onClick={downloadAllConfigurations}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="download-all-btn"
              title="Download all configurations as ZIP"
            >
              <PackageOpen className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Download All</span>
              <span className="sm:hidden">All</span>
            </Button>
            <label className="w-full">
              <Button
                variant="outline"
                size="sm"
                className="w-full cursor-pointer"
                asChild
                data-testid="import-btn"
              >
                <span>
                  <Upload className="w-4 h-4 mr-1" />
                  Import
                </span>
              </Button>
              <input
                type="file"
                accept=".json"
                onChange={importConfiguration}
                className="hidden"
              />
            </label>
            <Button
              onClick={loadProfiles}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>

          {/* Profile Name and Suite Number */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="profile-name">Profile Name *</Label>
              <Input
                id="profile-name"
                value={profileName}
                onChange={(e) => handleProfileNameChange(e.target.value)}
                placeholder="e.g., Suite 101 - Building A"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label htmlFor="suite-number">Suite Number</Label>
              <Input
                id="suite-number"
                value={suiteNumber}
                onChange={(e) => setSuiteNumber(e.target.value)}
                placeholder="e.g., 101"
                data-testid="suite-number-input"
                title="Auto-fills from profile name but can be changed"
              />
            </div>
          </div>
        </div>

        {/* Profile Selector */}
        <ProfileSelector
          profiles={profiles}
          currentConfigId={currentConfigId}
          onLoadProfile={loadConfiguration}
          onLoadAsTemplate={loadConfigurationAsTemplate}
          onRefresh={loadProfiles}
          isNewConfigMode={isNewConfigMode}
        />

        {/* Controller Section */}
        <ControllerSection
          controller={controller}
          setController={setController}
        />

        {/* Advanced Section */}
        <AdvancedSection
          advanced={advanced}
          setAdvanced={setAdvanced}
          onSaveDefaults={saveAsDefaults}
          onApplyDefaults={applyDefaults}
        />

        {/* Device Sections */}
        <DeviceSection
          title="Lights"
          devices={lights}
          setDevices={setLights}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'type', label: 'Type', type: 'select', options: ['Dimmer', 'Relay', 'RGB', 'Other'] },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' }
          ]}
        />

        <DeviceSection
          title="Shades"
          devices={shades}
          setDevices={setShades}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' }
          ]}
        />

        <DeviceSection
          title="Climate"
          devices={climate}
          setDevices={setClimate}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' },
            { name: 'zone', label: 'Zone', type: 'text' }
          ]}
        />

        <DeviceSection
          title="Audio"
          devices={audio}
          setDevices={setAudio}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'type', label: 'Type', type: 'select', options: ['Speaker', 'Amplifier', 'DSP', 'Microphone', 'Other'] },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' },
            { name: 'zone', label: 'Zone', type: 'text' }
          ]}
        />

        <DeviceSection
          title="Video"
          devices={video}
          setDevices={setVideo}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'type', label: 'Type', type: 'select', options: ['Display', 'Projector', 'Camera', 'Switcher', 'Other'] },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' },
            { name: 'input_output', label: 'Input/Output', type: 'text' }
          ]}
        />

        <DeviceSection
          title="Relays"
          devices={relays}
          setDevices={setRelays}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'type', label: 'Type', type: 'select', options: ['NO', 'NC', 'Latching', 'Momentary'] },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' },
            { name: 'function', label: 'Function', type: 'text' }
          ]}
        />

        <DeviceSection
          title="Security"
          devices={security}
          setDevices={setSecurity}
          fields={[
            { name: 'name', label: 'Name', type: 'text' },
            { name: 'type', label: 'Type', type: 'select', options: ['Motion', 'Door', 'Window', 'Keypad', 'Other'] },
            { name: 'room', label: 'Room', type: 'text' },
            { name: 'address', label: 'Address', type: 'text' },
            { name: 'zone', label: 'Zone', type: 'text' }
          ]}
        />

        {/* Custom Categories */}
        {Object.keys(customDevices).map(categoryName => {
          const category = customCategories.find(c => c.category_name === categoryName);
          if (!category) return null;
          
          return (
            <DeviceSection
              key={categoryName}
              title={categoryName}
              devices={customDevices[categoryName] || []}
              setDevices={(devices) => {
                setCustomDevices(prev => ({
                  ...prev,
                  [categoryName]: devices
                }));
              }}
              fields={category.fields}
            />
          );
        })}

        {/* Add Custom Category Button */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Custom Device Categories</h2>
            <Button
              onClick={() => setShowAddCategoryDialog(true)}
              variant="outline"
              size="sm"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Category
            </Button>
          </div>
          
          {customCategories.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">Your custom categories:</p>
              <div className="flex flex-wrap gap-2">
                {customCategories.map(category => (
                  <div
                    key={category.id}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                  >
                    {category.category_name}
                    <button
                      onClick={() => deleteCustomCategory(category.id)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Delete category"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Add Category Dialog */}
      <AddCategoryDialog
        isOpen={showAddCategoryDialog}
        onClose={() => setShowAddCategoryDialog(false)}
        onSave={createCustomCategory}
      />
      
      <Toaster />
    </div>
  );
};

export default Configurator;